import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useEffect } from 'react';
import {
  View,
  Image,
  StyleSheet,
  Animated,
  Dimensions,
  StatusBar,
  Text,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { onboardingService } from '../../services/onboarding';
import { API_URL } from '../../config';
import { authLogger } from '../../utils/logger';
import { sessionManager } from '../../services/sessionManager';
import { wizardScreenForStep, letterScreenForStep } from '../../navigation/postAuthRoute';
import { shouldRouteToUnlockAtSignIn } from '../../navigation/postAuthUnlockRoute';

type RootStackParamList = {
  Onboarding: undefined;
  SignIn: undefined;
  SignUp: undefined;
};

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const { width, height } = Dimensions.get('window');

export const SplashScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const fadeAnim = new Animated.Value(0);
  const scaleAnim = new Animated.Value(0.95);
  const taglineFadeAnim = new Animated.Value(0);

  useEffect(() => {
    // Animate logo fade in and scale up
    Animated.sequence([
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          tension: 20,
          friction: 7,
          useNativeDriver: true,
        }),
      ]),
      // Fade in tagline after logo animation
      Animated.timing(taglineFadeAnim, {
        toValue: 1,
        duration: 800,
        delay: 200,
        useNativeDriver: true,
      }),
    ]).start();

    // Check authentication state and navigate accordingly
    const checkAccountAndNavigate = async () => {
      try {
        const token = await getAuthToken();
        const emailVerified = await AsyncStorage.getItem('@email_verified');
        const phone2FASetup = await AsyncStorage.getItem('@2fa_setup_complete');
        const hasCompletedOnboarding = await AsyncStorage.getItem('hasCompletedOnboarding');
        const hasLoggedIn = await AsyncStorage.getItem('hasLoggedIn');
        const pendingInvitationToken = await AsyncStorage.getItem('@invitation_token');

        authLogger.debug('=== SPLASH SCREEN AUTH STATE ===');
        authLogger.debug('Token:', token ? 'YES' : 'NO');
        authLogger.debug('Email Verified:', emailVerified);
        authLogger.debug('2FA Setup Complete:', phone2FASetup);
        authLogger.debug('Onboarding Complete:', hasCompletedOnboarding);
        authLogger.debug('Has Logged In:', hasLoggedIn);
        authLogger.debug('Pending Invitation Token:', pendingInvitationToken ? 'YES' : 'NO');

        // No token - check if user has completed signup process
        if (!token) {
          // A verified account belongs on Login. Security setup (TOTP/SMS)
          // is optional for letter-first accounts, so it is not part of
          // "has this person signed up".
          if (emailVerified === 'true') {
            authLogger.debug('→ Signup complete but not logged in, navigate to Login');
            navigation.replace('Login');
            return;
          }
          // Otherwise, new user → SignUp
          authLogger.debug('→ No account, navigate to SignUp');
          navigation.replace('SignUp');
          return;
        }

        // Has token but email not verified → EmailVerificationNotice
        if (token && emailVerified !== 'true') {
          authLogger.debug('→ Account exists but email not verified');
          const signupEmail = await AsyncStorage.getItem('@signup_email');
          if (signupEmail) {
            navigation.replace('EmailVerificationNotice', { email: signupEmail });
          } else {
            // No email stored, go to SignUp
            navigation.replace('SignUp');
          }
          return;
        }

        // Security setup (TOTP/SMS) is never forced on a cold start. It is
        // offered when the user moves up a level (SecurityOptions after an
        // upgrade) and stays available in Settings (owner decision
        // 2026-09-07). Legacy accounts that were mid-setup are still
        // routed through the Security Chooser below.
        const isLetterFirst = await (async () => {
          try {
            const raw = await AsyncStorage.getItem('user');
            return ((JSON.parse(raw ?? '{}')?.onboardingVersion ?? 0) as number) >= 2;
          } catch {
            return false;
          }
        })();
        if (phone2FASetup !== 'true') {
          authLogger.debug('→ 2FA not set up; continuing without forcing setup');
        }

        // Verified account with a token → check token freshness, then invitation + onboarding
        if (token && emailVerified === 'true') {
          authLogger.debug('→ Auth complete, checking token freshness...');

          // Check if JWT is expired or about to expire — refresh via device trust
          const needsRefresh = await sessionManager.isTokenExpiredOrExpiring();
          if (needsRefresh) {
            authLogger.debug('→ Token expired/expiring, attempting refresh...');
            const refreshed = await sessionManager.tryRefreshToken();
            if (refreshed) {
              authLogger.debug('→ Token refreshed successfully');
            } else {
              // Device trust expired too — must re-login
              authLogger.debug('→ Token refresh failed, clearing session and redirecting to Login');
              await AsyncStorage.multiRemove([
                'token', '@auth_token', '@refresh_token',
                'user', 'hasLoggedIn',
              ]);
              navigation.replace('Login');
              return;
            }
          }

          authLogger.debug('→ Token valid, verifying TOTP status server-side...');

          // Server-side TOTP check to prevent bypass via app close/reopen
          try {
            let userEmail = await AsyncStorage.getItem('@user_email');
            if (!userEmail) {
              userEmail = await AsyncStorage.getItem('@signup_email');
            }
            if (!userEmail) {
              const userJson = await AsyncStorage.getItem('user');
              if (userJson) {
                const userData = JSON.parse(userJson);
                userEmail = userData.email;
              }
            }

            if (userEmail) {
              const totpCheckResponse = await fetch(`${API_URL}/auth/check-totp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmail }),
              });

              if (totpCheckResponse.ok) {
                const totpData = await totpCheckResponse.json();

                // Two-factor is never forced from a cold start (owner rule):
                // it is offered after an upgrade and lives in Settings. The
                // server-side status is logged for diagnostics only.
                authLogger.debug(
                  `-> TOTP enabled server-side: ${totpData.isTotpEnabled ? 'yes' : 'no'} (letter-first: ${isLetterFirst})`,
                );
              }
            }
          } catch (totpError) {
            authLogger.error('TOTP check in splash failed:', totpError);
            // Proceed normally — TOTP check is best-effort for offline scenarios
          }

          // Check for pending invitation BEFORE onboarding
          if (pendingInvitationToken) {
            authLogger.debug('→ Pending invitation found, redirecting to AcceptInvitation');
            navigation.reset({
              index: 0,
              routes: [{
                name: 'AcceptInvitation',
                params: { token: pendingInvitationToken }
              }],
            });
            return;
          }

          try {
            // Fetch legacy progress AND new-wizard state in one call
            const { progress, onboardingVersion, onboardingStep } =
              await onboardingService.getProgressAndWizardState();

            // Letter-first flow takes precedence: version-2 users who
            // haven't completed onboarding resume the letter flow.
            if (onboardingVersion >= 2 && onboardingStep !== 'complete') {
              authLogger.debug(
                `→ Letter-first flow detected (version=${onboardingVersion}, step=${onboardingStep}), routing to LetterFirst`,
              );
              // Cast: this screen's navigation prop is typed against the
              // auth stack, which doesn't know root routes (same reason
              // the OnboardingWizard reset below type-errors). Keeps the
              // tsc baseline flat instead of adding a 17th instance.
              navigation.reset({
                index: 0,
                routes: [{
                  name: 'LetterFirst',
                  state: {
                    routes: [{ name: letterScreenForStep(onboardingStep ?? 'letter') }],
                  },
                } as never],
              });
              return;
            }

            // New-wizard flow takes precedence: if this user is
            // version 1 and hasn't completed the wizard, route them in.
            if (onboardingVersion >= 1 && onboardingStep !== 'complete') {
              authLogger.debug(
                `→ New wizard flow detected (version=${onboardingVersion}, step=${onboardingStep}), routing to OnboardingWizard`,
              );
              navigation.reset({
                index: 0,
                routes: [{
                  name: 'OnboardingWizard',
                  state: {
                    routes: [{ name: wizardScreenForStep(onboardingStep ?? 'profile') }],
                  },
                }],
              });
              return;
            }

            if (!progress) {
              // Check if token was cleared by a 401 during the API call (session expired)
              const currentToken = await getAuthToken();
              if (!currentToken) {
                authLogger.debug('→ Session expired (token cleared), navigate to Login');
                await AsyncStorage.removeItem('@email_verified');
                await AsyncStorage.removeItem('@2fa_setup_complete');
                await AsyncStorage.removeItem('user');
                await AsyncStorage.removeItem('hasLoggedIn');
                navigation.replace('Login');
                return;
              }
              // No onboarding started yet → go to AIOnboardingWelcome
              authLogger.debug('→ No onboarding found, navigate to AIOnboardingWelcome');
              navigation.replace('AIOnboardingWelcome');
              return;
            }

            // If onboarding was skipped or completed, go straight to Dashboard
            // — unless the user has enrolled a recovery code and the unwrapped
            // key isn't cached on this device. In that case we route to
            // UnlockVaultScreen first; Dashboard opens only after unlock.
            if (progress.isSkipped || progress.isCompleted) {
              if (await shouldRouteToUnlockAtSignIn()) {
                authLogger.debug('→ Vault locked, navigate to UnlockVault');
                navigation.reset({
                  index: 0,
                  routes: [
                    { name: 'Auth', state: { routes: [{ name: 'UnlockVault' }] } },
                  ],
                });
                return;
              }
              authLogger.debug('→ Onboarding skipped or complete, navigate to Dashboard');
              navigation.reset({
                index: 0,
                routes: [{ name: 'Dashboard' }],
              });
              return;
            }

            if (progress.isPaused) {
              // Onboarding paused → go to AIOnboardingPaused
              authLogger.debug('→ Onboarding paused, navigate to AIOnboardingPaused');
              navigation.replace('AIOnboardingPaused');
              return;
            }

            // Onboarding in progress (not paused, not skipped, not completed) → resume
            authLogger.debug('→ Onboarding in progress, navigate to AIOnboardingWelcome');
            navigation.replace('AIOnboardingWelcome');
            return;
          } catch (error) {
            authLogger.error('Error checking onboarding status:', error);
            // Re-check if token still exists (may have been cleared by 401 interceptor)
            const currentToken = await getAuthToken();
            if (currentToken) {
              if (await shouldRouteToUnlockAtSignIn()) {
                authLogger.debug('→ Vault locked after onboarding error, navigate to UnlockVault');
                navigation.reset({
                  index: 0,
                  routes: [
                    { name: 'Auth', state: { routes: [{ name: 'UnlockVault' }] } },
                  ],
                });
              } else {
                authLogger.debug('→ Error checking onboarding but user authenticated, navigate to Dashboard');
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'Dashboard' }],
                });
              }
            } else {
              authLogger.debug('→ Session expired, navigate to Login');
              await AsyncStorage.removeItem('@email_verified');
              await AsyncStorage.removeItem('@2fa_setup_complete');
              await AsyncStorage.removeItem('user');
              await AsyncStorage.removeItem('hasLoggedIn');
              navigation.replace('Login');
            }
            return;
          }
        }

        // Fallback: If we get here, something is in an unexpected state
        authLogger.debug('→ Unexpected state, fallback to SignUp');
        navigation.replace('SignUp');

      } catch (error) {
        authLogger.error('Error checking auth state:', error);
        // Fallback to SignUp on error
        navigation.replace('SignUp');
      }
    };

    const timer = setTimeout(() => {
      checkAccountAndNavigate();
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor="#FFFFFF" />
      <View style={styles.content}>
        <Animated.View
          style={[
            styles.logoContainer,
            {
              opacity: fadeAnim,
              transform: [{ scale: scaleAnim }],
            },
          ]}
        >
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </Animated.View>

        <Animated.View
          style={[
            styles.taglineContainer,
            {
              opacity: taglineFadeAnim,
            },
          ]}
        >
          <Text style={styles.taglineText}>Your Legacy,</Text>
          <Text style={styles.taglineText}>Your Way</Text>
        </Animated.View>
      </View>

      <Image
        source={require('../../assets/ornament.png')}
        style={styles.ornament}
        resizeMode="contain"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: height * 0.1,
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: ms(20),
  },
  logo: {
    width: width * 0.5,
    height: width * 0.5,
  },
  taglineContainer: {
    alignItems: 'center',
    marginTop: ms(20),
  },
  taglineText: {
    fontSize: ms(24, 0.3),
    color: '#62656b',
    fontWeight: '400',
    textAlign: 'center',
    lineHeight: ms(32, 0.3),
  },
  ornament: {
    width: width,
    height: height * 0.3,
    position: 'absolute',
    bottom: 0,
    left: 0,
    opacity: 0.5,
  },
});

export default SplashScreen; 